import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const F = ({ label, hint, children }) => (
  <label className="block space-y-1">
    <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">{label}</span>
    {children}
    {hint && <span className="block text-[11px] text-slate-500">{hint}</span>}
  </label>
);

const Shell = ({ open, onClose, title, desc, testId, children }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-md bg-slate-900 border-slate-800" data-testid={testId}>
      <DialogHeader>
        <DialogTitle className="font-heading text-xl">{title}</DialogTitle>
        {desc && <DialogDescription>{desc}</DialogDescription>}
      </DialogHeader>
      {children}
    </DialogContent>
  </Dialog>
);

const useSubmit = () => {
  const [busy, setBusy] = useState(false);
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return [busy, run];
};

const EMPTY = { store_name: "", name: "", email: "", password: "", expires_at: "", card_limit: "", plan_id: "" };
const summary = (p) => `${p.card_limit ? `${p.card_limit.toLocaleString()} cards` : "Unlimited"} · ${p.duration_months} mo`;

export function CreateStoreDialog({ open, onClose, onCreated, plans = [] }) {
  const [f, setF] = useState(EMPTY);
  const [busy, run] = useSubmit();
  useEffect(() => { if (open) setF(EMPTY); }, [open]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = (e) => {
    e.preventDefault();
    run(async () => {
      const body = { ...f, plan_id: f.plan_id || null, expires_at: f.expires_at || null, card_limit: f.card_limit ? Number(f.card_limit) : null };
      const { data } = await api.post("/admin/stores", body);
      onCreated(data);
      toast.success(`Store ${data.store_name || data.email} created`);
      onClose();
    });
  };
  return (
    <Shell open={open} onClose={onClose} title="New store" desc="The store can sign in right away with these details." testId="create-store-dialog">
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <F label="Store name"><input className="cf-input h-9 text-sm" value={f.store_name} onChange={set("store_name")} placeholder="New Amina Jewellers" data-testid="new-store-name-input" /></F>
          <F label="Owner name"><input className="cf-input h-9 text-sm" value={f.name} onChange={set("name")} data-testid="new-store-owner-input" /></F>
        </div>
        <F label="Login email"><input type="email" required className="cf-input h-9 text-sm" value={f.email} onChange={set("email")} data-testid="new-store-email-input" /></F>
        <F label="Password"><input required minLength={6} className="cf-input h-9 text-sm font-mono" value={f.password} onChange={set("password")} data-testid="new-store-password-input" /></F>
        <F label="Plan package" hint={f.plan_id ? "Limit and expiry come from the package" : "Or set a custom expiry and limit below"}>
          <select className="cf-input h-9 text-sm" value={f.plan_id} onChange={set("plan_id")} data-testid="new-store-plan-select">
            <option value="">Custom (no package)</option>
            {plans.map((p) => <option key={p.id} value={p.id}>{p.name} — {summary(p)}{p.price ? ` · ${p.price}` : ""}</option>)}
          </select>
        </F>
        {!f.plan_id && <div className="grid grid-cols-2 gap-3">
          <F label="Plan expiry" hint="Leave empty for no expiry"><input type="date" className="cf-input h-9 text-sm" value={f.expires_at} onChange={set("expires_at")} data-testid="new-store-expiry-input" /></F>
          <F label="Card limit" hint="Leave empty for unlimited"><input type="number" min={1} className="cf-input h-9 text-sm" value={f.card_limit} onChange={set("card_limit")} data-testid="new-store-limit-input" /></F>
        </div>}
        <button disabled={busy} className="cf-btn-primary w-full h-10" data-testid="new-store-submit">{busy && <Loader2 size={14} className="animate-spin" />} Create store</button>
      </form>
    </Shell>
  );
}

export function PlanDialog({ store, onClose, onSaved, plans = [] }) {
  const [exp, setExp] = useState("");
  const [lim, setLim] = useState("");
  const [busy, run] = useSubmit();
  useEffect(() => { if (store) { setExp(store.expires_at || ""); setLim(store.card_limit || ""); } }, [store]);
  const submit = (e) => {
    e.preventDefault();
    run(async () => {
      const body = { clear_expiry: !exp, expires_at: exp || null };
      if (String(lim || "") !== String(store.card_limit || "")) Object.assign(body, { clear_limit: !lim, card_limit: lim ? Number(lim) : null });
      onSaved((await api.patch(`/admin/stores/${store.id}`, body)).data);
      toast.success("Plan updated");
      onClose();
    });
  };
  const apply = (p) => run(async () => {
    onSaved((await api.post(`/admin/stores/${store.id}/apply-plan`, { plan_id: p.id })).data);
    toast.success(`${p.name} package applied`);
    onClose();
  });
  const addMonths = (m) => {
    const base = exp && exp > new Date().toISOString().slice(0, 10) ? new Date(exp) : new Date();
    base.setMonth(base.getMonth() + m);
    setExp(base.toISOString().slice(0, 10));
  };
  return (
    <Shell open={!!store} onClose={onClose} title="Plan & limits" desc={store && `${store.store_name || store.email} · ${store.cards} cards saved`} testId="plan-dialog">
      {plans.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400">Apply a package{store?.plan_name && <span className="text-amber-300"> · current: {store.plan_name}</span>}</p>
          <div className="grid grid-cols-3 gap-2">
            {plans.map((p, i) => (
              <button key={p.id} type="button" disabled={busy} onClick={() => apply(p)} data-testid={`apply-plan-${i}`}
                className="text-left rounded-lg border border-slate-800 hover:border-amber-400 p-2.5 transition-colors">
                <p className="text-sm font-bold" style={{ color: p.color }}>{p.name}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">{summary(p)}</p>
              </button>
            ))}
          </div>
          <p className="text-[11px] text-slate-500">Extends from the current expiry (or today) by the package duration.</p>
          <div className="border-t border-slate-800 pt-3 text-[10px] font-mono uppercase tracking-wider text-slate-500">Or set custom</div>
        </div>
      )}
      <form onSubmit={submit} className="space-y-4">
        <F label="Plan expiry" hint="The store can't sign in after this date. Empty = never expires.">
          <div className="flex gap-2">
            <input type="date" className="cf-input h-9 text-sm" value={exp} onChange={(e) => setExp(e.target.value)} data-testid="plan-expiry-input" />
            <button type="button" className="cf-btn-ghost h-9 text-xs shrink-0" onClick={() => setExp("")} data-testid="plan-expiry-clear">Clear</button>
          </div>
        </F>
        <div className="flex gap-2">
          {[1, 3, 6, 12].map((m) => <button type="button" key={m} className="cf-btn-ghost h-7 text-[11px]" onClick={() => addMonths(m)} data-testid={`plan-extend-${m}`}>+{m} mo</button>)}
        </div>
        <F label="Card limit" hint="Total cards the store can save. Empty = unlimited.">
          <input type="number" min={1} className="cf-input h-9 text-sm" value={lim} onChange={(e) => setLim(e.target.value)} placeholder="Unlimited" data-testid="plan-limit-input" />
        </F>
        <button disabled={busy} className="cf-btn-primary w-full h-10" data-testid="plan-save-button">{busy && <Loader2 size={14} className="animate-spin" />} Save plan</button>
      </form>
    </Shell>
  );
}

export function PasswordDialog({ store, onClose }) {
  const [pw, setPw] = useState("");
  const [busy, run] = useSubmit();
  useEffect(() => { if (store) setPw(""); }, [store]);
  const submit = (e) => {
    e.preventDefault();
    run(async () => {
      await api.post(`/admin/stores/${store.id}/reset-password`, { password: pw });
      toast.success("Password reset — share the new password with the store");
      onClose();
    });
  };
  return (
    <Shell open={!!store} onClose={onClose} title="Reset password" desc={store?.email} testId="password-dialog">
      <form onSubmit={submit} className="space-y-3">
        <F label="New password" hint="At least 6 characters. Any failed-login lock is cleared too.">
          <input required minLength={6} className="cf-input h-9 text-sm font-mono" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" />
        </F>
        <button disabled={busy} className="cf-btn-primary w-full h-10" data-testid="reset-password-submit">{busy && <Loader2 size={14} className="animate-spin" />} Set new password</button>
      </form>
    </Shell>
  );
}
